/**
 * PaymentGateway
 *
 * Handle listing activation for free listings
 *
 * @returns onChargeAndActivateListingResponse as ChargeAndActivateListing
 *
 */
import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyActivateWebApiHandler_activate } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { Alert, Heading, Modal } from '@zillow/constellation-10';

import ErrorMessageConstants from '../../constants/ErrorMessageConstants';
import { ACTIVATE_LISTING_MUTATION_KEY } from '../../constants/QueryConstants';

import SingleFamilyContractDetails from '../../model/SingleFamilyContractDetails';
import PayAndPublishCSEvents from '../../model/ClickstreamEvent/PayAndPublishCSEvents';
import ChargeAndActivateListing from '../../model/ChargeAndActivateListing';
import PreActivationModal from '../Modal/PreActivationModal';

const propTypes = {
  showPreActivationModal: PropTypes.bool,
  updateShowPreActivationModal: PropTypes.func,
  onChargeAndActivateListingResponse: PropTypes.func,
  onPreActivationModalSubmit: PropTypes.func,
};

const defaultProps = {
  showPreActivationModal: false,
  updateShowPreActivationModal: () => {},
  onChargeAndActivateListingResponse: () => {},
  onPreActivationModalSubmit: null,
};

const PaymentGateway = ({
  showPreActivationModal,
  updateShowPreActivationModal,
  onChargeAndActivateListingResponse,
  onPreActivationModalSubmit,
}) => {
  const queryClient = useQueryClient();

  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const [activationErrorMessage, setActivationErrorMessage] = useState(null);

  const { listingAlias, singleFamilyContract } = currentProperty;
  const mustSetActiveContract = singleFamilyContract.isRequired && !singleFamilyContract.isActive;

  const {
    data: contractsData,
    isLoading: isLoadingContracts,
    error: contractsError,
  } = useRentalManagerUserSingleFamilyEnterpriseContractWebApiHandler_contracts({
    enabled: mustSetActiveContract,
  });

  const contracts = contractsData?.contracts?.map(SingleFamilyContractDetails) ?? [];
  const contractErrorMessage = contractsError
    ? ErrorMessageConstants.SINGLE_FAMILY_CANNOT_RETRIEVE_CONTRACTS
    : mustSetActiveContract && contractsData && contracts.length === 0
      ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT
      : null;

  const { mutate: activateListing } = useRentalManagerUserPropertyActivateWebApiHandler_activate({
    mutationKey: ACTIVATE_LISTING_MUTATION_KEY,
  });

  const handleDefaultActivateListing = () => {
    activateListing(
      { getParams: { listingAlias } },
      {
        onSuccess: (response) => {
          PayAndPublishCSEvents.ActivateListing({ currentProperty, user });

          onChargeAndActivateListingResponse({
            success: true,
            response: ChargeAndActivateListing({
              activation: {
                dateActivatedTimestamp: response?.activatedMs,
                dateUpdatedTimestamp: response?.updatedMs,
                expirationDateTimestamp: response?.expirationMs,
                ...response,
              },
            }),
          });

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          const newErrorMessage =
            error?.errorDetails?.listingActivationFailed && error?.errorDetails?.activationError?.message
              ? error.errorDetails.activationError.message
              : error?.message;
          setActivationErrorMessage(newErrorMessage);
        },
      },
    );
  };

  return (
    <div data-testid="PreActivationModalWrapper">
      {(activationErrorMessage || (contractErrorMessage && showPreActivationModal)) && (
        <Modal
          open={true}
          onOpenChange={(open) =>
            !open &&
            (() => {
              setActivationErrorMessage(null);
            })()
          }
          header={<Heading level={3}>Something went wrong</Heading>}
          body={
            <div data-testid="PreActivationModalWrapper-error">
              <Alert tone="critical">{activationErrorMessage || contractErrorMessage}</Alert>
            </div>
          }
        />
      )}
      {showPreActivationModal && !isLoadingContracts && (
        <PreActivationModal
          onClose={() => updateShowPreActivationModal(false)}
          onSubmit={onPreActivationModalSubmit || handleDefaultActivateListing}
          contracts={contracts}
        />
      )}
    </div>
  );
};

PaymentGateway.propTypes = propTypes;
PaymentGateway.defaultProps = defaultProps;

export default PaymentGateway;
